// src/ClosetPage.js - Collapsible Row Design
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  TextField,
  IconButton,
  Button,
  Chip,
  Avatar,
  Collapse,
  Grid,
  Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import VerifiedIcon from '@mui/icons-material/Verified';
import RefreshIcon from '@mui/icons-material/Refresh';
import { supabase } from './supabaseClient';
import ProfileDialog from './ProfileDialog';

import { ink, surface, statusTone } from './designTokens';
import { prettyStatus } from './labels';
export default function ClosetPage() {
  const [groupedUsers, setGroupedUsers] = useState([]);
  const [allGroupedUsers, setAllGroupedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogPhone, setDialogPhone] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedRows, setExpandedRows] = useState(new Set());

  const buildPublicUrl = useCallback((bucket, path) => {
    if (!path) return null;
    const trimmed = path.replace(/^https?:\/\/[^/]+\/storage\/v1\/object\/public\//, '');
    if (trimmed.startsWith('http')) return trimmed;
    const clean = trimmed.replace(/^\/+/, '');
    const base = process.env.REACT_APP_SUPABASE_URL;
    return `${base}/storage/v1/object/public/${bucket}/${clean}`;
  }, []);

  const normalizeProfileImage = useCallback((img) => {
    if (!img) return null;
    if (img.startsWith('http')) return img;
    const clean = img.startsWith('profiles/') ? img : `profiles/${img}`;
    return buildPublicUrl('profile-images', clean);
  }, [buildPublicUrl]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('Closet Items')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const itemsByUser = new Map();
      const phoneNumbers = [...new Set((data || []).map(item => item.phone_number).filter(Boolean))];

      // Fetch profiles with identity_verified
      const profileMap = new Map();
      if (phoneNumbers.length > 0) {
        const { data: profiles, error: profilesError } = await supabase
          .from('Profiles')
          .select('id, first_name, last_name, phone_number, email, profile_image, user_type, identity_verified')
          .in('phone_number', phoneNumbers);

        if (!profilesError && profiles) {
          profiles.forEach(profile => {
            const profile_image_url = normalizeProfileImage(profile.profile_image);
            profileMap.set(profile.phone_number, { ...profile, profile_image_url });
          });
        }
      }

      (data || []).forEach(item => {
        const phone = item.phone_number || 'Unknown';
        const profile = profileMap.get(phone) || {};
        
        if (!itemsByUser.has(phone)) {
          itemsByUser.set(phone, {
            phone,
            profile: profile,
            items: [],
          });
        }

        itemsByUser.get(phone).items.push(item);
      });

      const grouped = Array.from(itemsByUser.values())
        .map(group => ({
          ...group,
          items: group.items.sort((a, b) => 
            new Date(b.created_at || b.updated_at) - new Date(a.created_at || a.updated_at)
          ),
        }))
        .sort((a, b) => 
          new Date(b.items[0]?.created_at || b.items[0]?.updated_at) - 
          new Date(a.items[0]?.created_at || a.items[0]?.updated_at)
        );

      setGroupedUsers(grouped);
      setAllGroupedUsers(grouped);
      setExpandedRows(new Set());
    } catch (error) {
      console.error('Error fetching closet items:', error);
      setGroupedUsers([]);
      setAllGroupedUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!searchTerm) {
      setGroupedUsers(allGroupedUsers);
      return;
    }

    const term = searchTerm.toLowerCase();
    
    const filteredGroups = allGroupedUsers
      .map(group => {
        const userName = `${group.profile?.first_name || ''} ${group.profile?.last_name || ''}`.toLowerCase();
        const userMatch = userName.includes(term) || (group.phone || '').toLowerCase().includes(term);
        const items = group.items.filter(item => {
          return (
            userMatch ||
            item.item_name?.toLowerCase().includes(term) ||
            item.brand?.toLowerCase().includes(term) ||
            item.category?.toLowerCase().includes(term)
          );
        });
        return items.length > 0 ? { ...group, items } : null;
      })
      .filter(Boolean);

    setGroupedUsers(filteredGroups);
  }, [searchTerm, allGroupedUsers]);

  const toggleRow = (rowId) => {
    const rowIdString = String(rowId);
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(rowIdString)) {
      newExpanded.delete(rowIdString);
    } else {
      newExpanded.add(rowIdString);
    }
    setExpandedRows(newExpanded);
  };

  const expandAll = () => {
    const keys = groupedUsers.map(g => g.phone || 'unknown');
    setExpandedRows(new Set(keys));
  };

  const collapseAll = () => setExpandedRows(new Set());

  const closeProfile = () => setDialogPhone(null);

  const getImageUrl = (item) => {
    if (item.photo_url) return item.photo_url;
    if (item.image_url) return item.image_url;
    if (item.image) return item.image;
    return null;
  };

  const getUserTypeColor = (userType) => {
    switch (userType?.toLowerCase()) {
      case 'stylist':
        return 'primary';
      case 'client':
        return 'success';
      case 'admin':
        return 'secondary';
      default:
        return 'default';
    }
  };

  // Uses the shared statusTone() so every screen renders the same status the
  // same way. (The parameter used to be named `status`, which shadowed the
  // imported token of the same name and silently resolved to undefined.)
  const getStatusColor = (value) => {
    if (String(value || '').toLowerCase() === 'archived') {
      return { bg: surface.sunken, color: ink[900] };
    }
    const tone = statusTone(value);
    return { bg: tone.bg, color: tone.fg };
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, overflowX: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
            Closet Items ({groupedUsers.reduce((sum, g) => sum + g.items.length, 0)})
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage closet items
          </Typography>
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <IconButton onClick={fetchData}>
          <RefreshIcon />
        </IconButton>
      </Box>

      {/* Search and Filter Bar */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
          <TextField
            placeholder="Search by item, user, brand..."
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              ),
            }}
            sx={{ flexGrow: 1, minWidth: 250 }}
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" size="small" onClick={expandAll}>
              Expand All
            </Button>
            <Button variant="outlined" size="small" onClick={collapseAll}>
              Collapse All
            </Button>
            <Button variant="outlined" size="small" startIcon={<FilterListIcon />}>
              Filter
            </Button>
            <Button variant="outlined" size="small" startIcon={<FileDownloadIcon />}>
              Export
            </Button>
            <Button variant="contained" size="small" startIcon={<AddIcon />}>
              Add New Item
            </Button>
          </Box>
        </Box>
      </Paper>

      {groupedUsers.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="textSecondary" gutterBottom>
            No Closet Items Found
          </Typography>
          <Typography variant="body2" color="textSecondary">
            No closet items found in the database.
          </Typography>
        </Paper>
      ) : (
        <Paper sx={{ overflow: 'hidden' }}>
          {groupedUsers.map((group, idx) => {
            const rowId = group.phone || `user-${idx}`;
            const isExpanded = expandedRows.has(String(rowId));
            const userName = `${group.profile?.first_name || ''} ${group.profile?.last_name || ''}`.trim() || 'Unknown';
            const profileImage = group.profile?.profile_image_url || group.profile?.profile_image;
            const isVerified = group.profile?.identity_verified === true || group.profile?.identity_verified === 'true' || group.profile?.identity_verified === 1;

            return (
              <Box key={rowId}>
                {/* Summary Row */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 2,
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'action.hover' },
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                  onClick={() => toggleRow(rowId)}
                >
                  {/* Profile Image */}
                  <Box sx={{ mr: 2, flexShrink: 0 }}>
                    {profileImage ? (
                      <Avatar
                        src={profileImage}
                        alt={userName}
                        sx={{ width: 48, height: 48 }}
                      />
                    ) : (
                      <Avatar sx={{ width: 48, height: 48, bgcolor: 'grey.300' }}>
                        {userName !== 'Unknown' ? userName.charAt(0).toUpperCase() : '?'}
                      </Avatar>
                    )}
                  </Box>

                  {/* User Info */}
                  <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 3, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 150, flexShrink: 0 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {userName}
                      </Typography>
                      {isVerified && (
                        <VerifiedIcon color="primary" fontSize="small" />
                      )}
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 200, flexShrink: 0 }}>
                      {group.profile?.email || 'N/A'}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ minWidth: 150, flexShrink: 0, cursor: 'pointer', textDecoration: 'underline' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (group.phone) setDialogPhone(group.phone);
                      }}
                    >
                      {group.phone || 'N/A'}
                    </Typography>
                    <Chip
                      label={prettyStatus(group.profile?.user_type) || 'N/A'}
                      color={getUserTypeColor(group.profile?.user_type)}
                      size="small"
                      sx={{ minWidth: 80, flexShrink: 0 }}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100, flexShrink: 0 }}>
                      {group.items.length} item{group.items.length !== 1 ? 's' : ''}
                    </Typography>
                  </Box>

                  {/* Expand/Collapse Icon */}
                  <IconButton size="small" sx={{ flexShrink: 0 }}>
                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>

                {/* Expanded Closet Items */}
                <Collapse in={isExpanded}>
                  <Box sx={{ p: 3, backgroundColor: 'grey.50', borderTop: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      Closet Items ({group.items.length})
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Grid container spacing={2}>
                      {group.items.map((item) => {
                        const imageUrl = getImageUrl(item);
                        const status = item.status || 'Available';
                        const statusColors = getStatusColor(status);
                        return (
                          <Grid item xs={12} sm={6} md={4} lg={3} key={item.id}>
                            <Paper
                              elevation={1}
                              sx={{
                                p: 2,
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                '&:hover': { elevation: 3 },
                              }}
                            >
                              {imageUrl && (
                                <Box
                                  sx={{
                                    width: '100%',
                                    height: 200,
                                    borderRadius: 1,
                                    overflow: 'hidden',
                                    mb: 1,
                                    bgcolor: 'grey.200',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <Box
                                    component="img"
                                    src={imageUrl}
                                    alt={item.item_name || 'Item'}
                                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                  />
                                </Box>
                              )}
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                {item.item_name || 'Unnamed Item'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                                {item.brand || 'No brand'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                                {item.category || 'No category'}
                              </Typography>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
                                <Chip
                                  label={prettyStatus(status)}
                                  size="small"
                                  sx={{
                                    bgcolor: statusColors.bg,
                                    color: statusColors.color,
                                    fontWeight: 500,
                                    fontSize: '0.7rem',
                                  }}
                                />
                                <Box>
                                  <IconButton size="small" sx={{ color: 'text.secondary' }}>
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton size="small" sx={{ color: 'error.main' }}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </Box>
                              {item.created_at && (
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                  Added: {new Date(item.created_at).toLocaleDateString()}
                                </Typography>
                              )}
                            </Paper>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </Paper>
      )}

      <ProfileDialog
        open={Boolean(dialogPhone)}
        phoneNumber={dialogPhone}
        onClose={closeProfile}
      />
    </Box>
  );
}
